import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Flame, Snowflake, Wind, Wrench, Filter, ArrowRight } from 'lucide-react';

interface FAQ {
  question: string;
  answer: string;
}

interface Service {
  id: string;
  title: string;
  icon: any;
  description: string;
  details: string;
  faqs: FAQ[];
  image: string;
  imageAlt: string;
}

export default function Services() {
  const [openFAQs, setOpenFAQs] = useState<{ [key: string]: boolean }>({});

  const toggleFAQ = (serviceId: string, index: number) => {
    const key = `${serviceId}-${index}`;
    setOpenFAQs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const services: Service[] = [
    {
      id: 'furnace',
      title: 'Furnace Install & Replacement',
      icon: <Flame size={32} />,
      description: 'High-efficiency natural gas, propane, and oil furnace replacement.',
      image: 'https://images.unsplash.com/photo-1605281317010-fe5ffe798166?w=1200&q=80&auto=format&fit=crop',
      imageAlt: 'Modern high-efficiency residential furnace in a basement utility room',
      details: 'High-efficiency natural gas, propane, and oil furnace replacement. We size the unit to your actual heat load, not the size of the furnace you\'re replacing — older homes are often oversized by 30%, and a right-sized furnace runs longer cycles, costs less to operate, and lasts years longer. Brands we install: Lennox, Carrier, Goodman, Daikin Fit.',
      faqs: [
        {
          question: 'How do I know if my furnace needs replacement?',
          answer: 'If your furnace is over 15-20 years old, requires frequent repairs, makes unusual noises, or your heating bills have increased significantly, it may be time for replacement. We offer free assessments to help you make an informed decision.'
        },
        {
          question: 'What size furnace do I need?',
          answer: 'We perform a proper heat load calculation based on your home\'s square footage, insulation, windows, and other factors. Many older homes have oversized furnaces — we\'ll right-size yours for optimal efficiency and comfort.'
        },
        {
          question: 'How long does furnace installation take?',
          answer: 'Most furnace replacements are completed in one day (6-8 hours). Complex installations or ductwork modifications may require an additional day.'
        }
      ]
    },
    {
      id: 'ac-heat-pump',
      title: 'Central AC and Heat Pump Install',
      icon: <Snowflake size={32} />,
      description: 'Cooling-only central AC or cold-climate heat pumps.',
      image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&q=80&auto=format&fit=crop',
      imageAlt: 'Outdoor heat pump unit installed beside a residential home',
      details: 'Cooling-only central AC, or a cold-climate heat pump that handles both heating and cooling and qualifies for federal and Ontario rebates. We do the rebate paperwork for you.',
      faqs: [
        {
          question: 'What\'s the difference between AC and a heat pump?',
          answer: 'An AC unit only cools. A heat pump can both heat and cool your home, and modern cold-climate models work efficiently even in Ontario winters. Heat pumps qualify for government rebates up to $6,500.'
        },
        {
          question: 'What rebates are available?',
          answer: 'The Canada Greener Homes Grant offers up to $5,000 for qualifying heat pumps, plus an additional $600 for home energy evaluations. Ontario offers additional rebates. We handle all the paperwork for you.'
        },
        {
          question: 'Will a heat pump work in winter?',
          answer: 'Yes! Modern cold-climate heat pumps work efficiently down to -25°C. Many York Region homes use them as their primary heating source, with significant energy savings compared to traditional heating.'
        }
      ]
    },
    {
      id: 'ductless',
      title: 'Ductless Mini-Split Systems',
      icon: <Wind size={32} />,
      description: 'Perfect for additions, basements, and homes without ductwork.',
      image: 'https://images.unsplash.com/photo-1631545806609-2dceae6cd9b1?w=1200&q=80&auto=format&fit=crop',
      imageAlt: 'Wall-mounted ductless mini-split indoor unit',
      details: 'For additions, finished basements, and homes without ductwork. Single-zone or multi-zone Mitsubishi and Daikin systems, professionally line-set and commissioned.',
      faqs: [
        {
          question: 'Where are mini-splits best used?',
          answer: 'Mini-splits are ideal for room additions, finished basements, garages, home offices, or any space where extending ductwork would be difficult or expensive. They\'re also great for homes without existing ductwork.'
        },
        {
          question: 'Are mini-splits energy efficient?',
          answer: 'Yes! Mini-splits are among the most efficient HVAC systems available, with SEER ratings often exceeding 20. They allow zone control, so you only heat or cool occupied spaces.'
        },
        {
          question: 'How many indoor units can I have?',
          answer: 'Most residential systems support up to 5 indoor units from a single outdoor unit. We\'ll design a system that matches your specific needs and budget.'
        }
      ]
    },
    {
      id: 'maintenance',
      title: 'Service, Repair, and Annual Maintenance',
      icon: <Wrench size={32} />,
      description: 'Keep your system running efficiently year-round.',
      image: 'https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?w=1200&q=80&auto=format&fit=crop',
      imageAlt: 'Technician adjusting a digital thermostat on the wall',
      details: 'Same-week service in most cases, same-day for no-heat or no-cool emergencies during winter and summer peak. Annual maintenance plans starting at $189/year for one system.',
      faqs: [
        {
          question: 'What\'s included in annual maintenance?',
          answer: 'Our maintenance includes: cleaning and inspection of all components, checking refrigerant levels, testing safety controls, replacing filters, checking electrical connections, and a full system performance report.'
        },
        {
          question: 'How often should I service my HVAC system?',
          answer: 'We recommend annual service for most systems — ideally in spring for AC and fall for furnaces. High-use systems or those over 10 years old may benefit from twice-yearly service.'
        },
        {
          question: 'Do you offer emergency service?',
          answer: 'Yes! We provide same-day emergency service for no-heat situations in winter and no-cool in summer. Call (905) 555-0142 for immediate assistance.'
        }
      ]
    },
    {
      id: 'air-quality',
      title: 'Indoor Air Quality',
      icon: <Filter size={32} />,
      description: 'Breathe cleaner, healthier air in your home.',
      image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=1200&q=80&auto=format&fit=crop',
      imageAlt: 'Clean HEPA air filter being held in front of an air handler',
      details: 'HEPA filtration, HRV/ERV install, whole-home humidifiers, UV air treatment. If anyone in your house has asthma, allergies, or respiratory issues, this is the conversation we want to have.',
      faqs: [
        {
          question: 'What indoor air quality solutions do you offer?',
          answer: 'We install HEPA filtration systems, HRVs/ERVs for fresh air exchange, whole-home humidifiers and dehumidifiers, UV germicidal lights, and electronic air cleaners.'
        },
        {
          question: 'Will air quality improvements help with allergies?',
          answer: 'Yes! HEPA filters remove 99.97% of particles including pollen, dust mites, and pet dander. Combined with proper humidity control and UV treatment, many clients see significant allergy relief.'
        },
        {
          question: 'What\'s an HRV/ERV?',
          answer: 'Heat Recovery Ventilators (HRV) and Energy Recovery Ventilators (ERV) bring fresh outdoor air into your home while exhausting stale air, recovering heat/energy in the process. Essential for tight, modern homes.'
        }
      ]
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-[var(--color-slate-blue)] text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Our Services</h1>
          <p className="text-xl text-gray-100 max-w-3xl">
            From emergency repairs to complete system replacements, we handle all your heating, cooling, and air quality needs with licensed technicians and transparent pricing.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {services.map((service, serviceIndex) => (
            <div
              key={service.id}
              id={service.id}
              className={`mb-16 pb-16 ${serviceIndex < services.length - 1 ? 'border-b border-gray-200' : ''}`}
            >
              <img
                src={service.image}
                alt={service.imageAlt}
                width={1200}
                height={600}
                loading="lazy"
                className="w-full rounded-lg mb-6 object-cover aspect-[2/1]"
              />
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-[var(--color-warm-amber)] text-white p-3 rounded-lg flex-shrink-0">
                  {service.icon}
                </div>
                <div className="flex-grow">
                  <h2 className="text-3xl font-bold mb-4 text-[var(--color-text-dark)]">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    {service.details}
                  </p>
                </div>
              </div>

              {/* FAQs for this service */}
              <div className="ml-0 md:ml-16">
                <h3 className="text-xl font-bold mb-4 text-[var(--color-text-dark)]">
                  Frequently Asked Questions
                </h3>
                <div className="space-y-3">
                  {service.faqs.map((faq, faqIndex) => {
                    const key = `${service.id}-${faqIndex}`;
                    const isOpen = openFAQs[key];

                    return (
                      <div key={faqIndex} className="bg-[var(--color-neutral-bg)] rounded-lg">
                        <button
                          onClick={() => toggleFAQ(service.id, faqIndex)}
                          className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-100 transition-colors rounded-lg"
                          aria-expanded={isOpen}
                          aria-controls={`faq-answer-${key}`}
                        >
                          <span className="font-medium text-[var(--color-text-dark)]">
                            {faq.question}
                          </span>
                          {isOpen ? (
                            <ChevronUp size={20} className="text-[var(--color-slate-blue)] flex-shrink-0" />
                          ) : (
                            <ChevronDown size={20} className="text-[var(--color-slate-blue)] flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div id={`faq-answer-${key}`} className="px-6 pb-4">
                            <p className="text-gray-600">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center text-[var(--color-text-dark)]">
            Transparent, Upfront Pricing
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-lg p-6 shadow-md text-center">
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">Service Calls</h3>
              <p className="text-3xl font-bold text-[var(--color-slate-blue)] mb-3">$149</p>
              <p className="text-gray-600">Diagnostic fee waived if you proceed with repairs</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md text-center">
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">Annual Maintenance</h3>
              <p className="text-3xl font-bold text-[var(--color-slate-blue)] mb-3">From $189</p>
              <p className="text-gray-600">Per system, includes spring AC & fall furnace service</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md text-center">
              <h3 className="text-xl font-bold mb-3 text-[var(--color-text-dark)]">Installations</h3>
              <p className="text-3xl font-bold text-[var(--color-slate-blue)] mb-3">Free Quote</p>
              <p className="text-gray-600">Written estimate before any work begins</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[var(--color-slate-blue)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">
            Need HVAC Service?
          </h2>
          <p className="text-xl mb-8 text-gray-100 max-w-2xl mx-auto">
            Whether it's an emergency repair or a new installation, we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-[var(--color-warm-amber)] text-white px-8 py-4 rounded text-lg font-semibold hover:bg-opacity-90 transition-colors"
            >
              Get Your Free Quote
              <ArrowRight size={20} />
            </Link>
            <Link 
              to="/summer-ac-tune-up" 
              className="inline-flex items-center justify-center gap-2 bg-white text-[var(--color-slate-blue)] px-8 py-4 rounded text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              $129 AC Tune-Up Special
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}